import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import {
    Box, Button, Card, Chip, CardActions, CardContent,
    Container, Divider, Grid, Typography, CircularProgress,
    ToggleButton, ToggleButtonGroup
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : null;

const tiers = [
    {
        title: 'Individual',
        plan: 'essential',
        monthlyPrice: 80,
        subtext: 'This is our most affordable plan to get started!',
        description: [
            'Unlimited sending',
            'Email marketing',
            'Send newsletters'
        ],
        highlight: false,
        themeBorder: 'linear-gradient(135deg, #d0f0fd 0%, #a0d9ff 100%)',
        themeColor: '#1976d2', // MUI primary roughly
        hoverColor: '#115293',
        ctaName: 'Get Started'
    },
    {
        title: 'Standard',
        plan: 'growth',
        subheader: 'Most Popular',
        monthlyPrice: 250,
        subtext: 'This is our most affordable plan to get started!',
        description: [
            'Unlimited sending',
            'Email marketing',
            'Send newsletters',
            'Marketing automation'
        ],
        highlight: true,
        themeBorder: 'linear-gradient(135deg, #e0ffe1 0%, #b3ffb5 100%)',
        themeColor: '#2e7d32', // MUI success roughly
        hoverColor: '#1b5e20',
        ctaName: 'Upgrade Now'
    },
    {
        title: 'Pro',
        plan: 'scale',
        monthlyPrice: 500,
        subtext: 'For larger business or those seeking advanced services.',
        description: [
            'Unlimited sending',
            'Email marketing',
            'Send newsletters',
            'Marketing automation',
            'Email support',
            'Free design services'
        ],
        highlight: false,
        isPro: true,
        themeBorder: 'linear-gradient(135deg, #f3e1ff 0%, #dab6ff 100%)',
        themeColor: '#9c27b0', // MUI secondary roughly
        hoverColor: '#7b1fa2',
        ctaName: 'Contact Sales'
    },
];

export default function Pricing() {
    const token = useSelector((state) => state.auth.token);
    const [loadingPlan, setLoadingPlan] = React.useState(null);
    const [billingCycle, setBillingCycle] = React.useState('monthly');

    const handleBillingChange = (event, newCycle) => {
        if (newCycle !== null) {
            setBillingCycle(newCycle);
        }
    };

    const handleCheckout = async (planKey) => {
        if (!token) return;
        setLoadingPlan(planKey);
        try {
            const dashboardUrl = `${window.location.origin}/#/dashboard`;
            const successUrl = `${dashboardUrl}?checkoutSuccess=true&session_id={CHECKOUT_SESSION_ID}`;
            const cancelUrl = `${dashboardUrl}?checkoutCanceled=true`;

            // Note: Ideally pass the billingCycle to the backend here if it supports yearly
            const { data } = await axios.post('/api/billing/checkout',
                { planKey, successUrl, cancelUrl, cycle: billingCycle },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (stripePromise && data.id) {
                const stripe = await stripePromise;
                const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
                if (error) throw error;
            } else if (data.url) {
                window.location.href = data.url;
            }
        } catch (err) {
            console.error('Checkout error:', err);
            alert('Unable to start checkout. Please try again.');
        } finally {
            setLoadingPlan(null);
        }
    };

    return (
        <Container
            id="pricing"
            sx={{
                pt: { xs: 4, sm: 8 },
                pb: { xs: 8, sm: 16 },
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 3, sm: 6 },
            }}
        >
            <Box
                sx={{
                    width: { sm: '100%', md: '60%' },
                    textAlign: 'center',
                }}
            >
                <Typography
                    component="h2"
                    variant="h3"
                    gutterBottom
                    sx={{ color: '#1B1B1B', fontWeight: 600 }}
                >
                    Choose Your Plan
                </Typography>
                <Typography variant="body1" sx={{ color: '#6C757D', mb: 4 }}>
                    Choose the right program for your business
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <ToggleButtonGroup
                        value={billingCycle}
                        exclusive
                        onChange={handleBillingChange}
                        aria-label="billing cycle"
                        sx={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '30px',
                            p: 0.5,
                            boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.05)',
                            border: 'none',
                            '& .MuiToggleButtonGroup-grouped': {
                                border: 0,
                                borderRadius: '25px',
                                px: 4,
                                py: 1,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                color: '#6C757D',
                                '&.Mui-selected': {
                                    backgroundColor: '#1B1B1B',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#000000',
                                    }
                                },
                            },
                        }}
                    >
                        <ToggleButton value="monthly" aria-label="monthly">
                            Monthly
                        </ToggleButton>
                        <ToggleButton value="yearly" aria-label="yearly">
                            Yearly
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Grid
                container
                spacing={3}
                sx={{ alignItems: 'stretch', justifyContent: 'center', width: '100%', maxWidth: '1000px' }}
            >
                {tiers.map((tier) => {
                    // Calculate yearly price as 10% less, multiplied by 12 months
                    const isYearly = billingCycle === 'yearly';
                    const price = isYearly ? Math.floor(tier.monthlyPrice * 0.9 * 12) : tier.monthlyPrice;
                    const period = isYearly ? '/ Year' : '/ Month';

                    const mainColor = tier.themeColor;

                    return (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={tier.title}
                            sx={{ display: 'flex' }}
                        >
                            <Card
                                sx={[
                                    {
                                        p: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        width: '100%',
                                        borderRadius: '15px',
                                        boxShadow: tier.highlight ? '0px 10px 40px rgba(0, 0, 0, 0.08)' : '0px 4px 20px rgba(0, 0, 0, 0.04)',
                                        border: '1px solid #FFFFFF',
                                        position: 'relative',
                                        overflow: 'visible',
                                    }
                                ]}
                            >
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '6px',
                                    background: tier.themeBorder,
                                    borderTopLeftRadius: '15px',
                                    borderTopRightRadius: '15px',
                                    zIndex: 0
                                }} />

                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 0, pt: 1, zIndex: 1 }}>
                                    {tier.highlight && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: '-45px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: mainColor,
                                            color: '#FFFFFF',
                                            padding: '4px 15px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            zIndex: 1
                                        }}>
                                            Most Popular
                                        </Box>
                                    )}

                                    <Typography component="h3" variant="h6" sx={{ fontWeight: 600, color: '#1B1B1B', mb: 2 }}>
                                        {tier.title}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'center',
                                            mb: 1
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '24px', fontWeight: 600, color: mainColor, position: 'relative', top: '-15px' }}>£</Typography>
                                        <Typography sx={{ fontSize: '48px', fontWeight: 700, color: mainColor }}>{price}</Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#6C757D', ml: 0.5 }}>{period}</Typography>
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#6C757D', fontSize: '12px', mb: 1.5 }}>
                                        {tier.subtext}
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        component={(!token || tier.plan === 'scale') ? RouterLink : 'button'}
                                        to={!token ? `/register?plan=${tier.plan}` : (tier.plan === 'scale' ? '/contact' : undefined)}
                                        onClick={token && tier.plan !== 'scale' ? () => handleCheckout(tier.plan) : undefined}
                                        sx={{
                                            mb: 3,
                                            py: 0.5,
                                            px: 2,
                                            borderRadius: '4px',
                                            border: `1px solid ${mainColor}`,
                                            color: mainColor,
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            '&:hover': {
                                                backgroundColor: mainColor,
                                                color: '#FFFFFF',
                                                borderColor: mainColor
                                            }
                                        }}
                                    >
                                        Try for free
                                    </Button>

                                    <Divider sx={{ width: '100%', mb: 3, opacity: 0.5 }} />

                                    <Box sx={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {tier.description.map((line) => (
                                            <Box
                                                key={line}
                                                sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
                                            >
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke={mainColor}
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    style={{ minWidth: '16px' }}
                                                >
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#495057', fontSize: '14px', textAlign: 'left' }}
                                                >
                                                    {line}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </CardContent>

                                <CardActions sx={{ p: 0, mt: 4, flexDirection: 'column' }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        disabled={loadingPlan === tier.plan}
                                        component={(!token || tier.plan === 'scale') ? RouterLink : 'button'}
                                        to={!token ? `/register?plan=${tier.plan}` : (tier.plan === 'scale' ? '/contact' : undefined)}
                                        onClick={token && tier.plan !== 'scale' ? () => handleCheckout(tier.plan) : undefined}
                                        sx={{
                                            py: 1.5,
                                            borderRadius: '8px',
                                            border: tier.highlight ? 'none' : `1px solid ${mainColor}`,
                                            backgroundColor: tier.highlight ? mainColor : 'transparent',
                                            color: tier.highlight ? '#FFFFFF' : mainColor,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            '&:hover': {
                                                backgroundColor: tier.highlight ? tier.hoverColor : mainColor,
                                                color: '#FFFFFF',
                                            }
                                        }}
                                    >
                                        {loadingPlan === tier.plan ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            tier.ctaName
                                        )}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Container>
    );
}
